import React, { useState, useEffect } from 'react';
import { 
  FaLeaf, 
  FaChartLine, 
  FaSearchPlus, 
  FaHistory, 
  FaDownload, 
  FaFilter, 
  FaMapMarkerAlt, 
  FaExclamationTriangle, 
  FaBullseye, 
  FaWater, 
  FaSeedling,
  FaCheckSquare,
  FaSquare,
  FaExchangeAlt,
  FaTimes,
  FaInfoCircle
} from 'react-icons/fa';

const CropEvaluation = () => {
  const [lands, setLands] = useState([
    {
      id: 1,
      name: 'North Field',
      location: 'Village A',
      size: '12 acres',
      currentCrop: 'Wheat',
      soilType: 'Clay Loam',
      lastRotation: 'March 2023',
      healthIndex: 87,
      sustainabilityScore: 82,
      yieldPrediction: '3.8 tons/acre',
      recommendations: [
        'Rotate to legumes next season to improve soil nitrogen content',
        'Increase organic matter by 2% through cover cropping',
        'Reduce irrigation by 15% based on soil moisture sensors',
        'Apply targeted micronutrients based on recent soil test results'
      ],
      history: [
        { crop: 'Wheat', season: 'Spring 2023', yield: '3.7 tons/acre' },
        { crop: 'Fallow', season: 'Winter 2022', yield: 'N/A' },
        { crop: 'Corn', season: 'Summer 2022', yield: '9.5 tons/acre' }
      ]
    },
    {
      id: 2,
      name: 'South Valley',
      location: 'Village B',
      size: '8 acres',
      currentCrop: 'Corn',
      soilType: 'Sandy Loam',
      lastRotation: 'May 2023',
      healthIndex: 72,
      sustainabilityScore: 68,
      yieldPrediction: '8.9 tons/acre',
      recommendations: [
        'Add cover crops after harvest to prevent soil erosion',
        'Implement contour farming to manage water runoff',
        'Test for micronutrient deficiencies, particularly zinc and boron',
        'Consider installing drip irrigation to improve water efficiency'
      ],
      history: [
        { crop: 'Corn', season: 'Summer 2023', yield: 'In progress' },
        { crop: 'Winter Wheat', season: 'Winter 2022', yield: '3.2 tons/acre' },
        { crop: 'Soybeans', season: 'Summer 2022', yield: '2.8 tons/acre' }
      ]
    },
    {
      id: 3,
      name: 'East Hillside',
      location: 'Village C',
      size: '15 acres',
      currentCrop: 'Rice',
      soilType: 'Silty Clay',
      lastRotation: 'April 2023',
      healthIndex: 65,
      sustainabilityScore: 58,
      yieldPrediction: '4.2 tons/acre',
      recommendations: [
        'Delay planting by 2 weeks to avoid early season pest pressure',
        'Implement integrated pest management to reduce chemical inputs',
        'Improve drainage system to prevent waterlogging during heavy rains',
        'Consider alternating with drought-resistant crops in rotation'
      ],
      history: [
        { crop: 'Rice', season: 'Spring 2023', yield: 'In progress' },
        { crop: 'Fallow', season: 'Winter 2022', yield: 'N/A' },
        { crop: 'Rice', season: 'Spring 2022', yield: '4.0 tons/acre' }
      ]
    },
    {
      id: 4,
      name: 'West Plateau',
      location: 'Village A',
      size: '10 acres',
      currentCrop: 'Soybeans',
      soilType: 'Loam',
      lastRotation: 'June 2023',
      healthIndex: 79,
      sustainabilityScore: 75,
      yieldPrediction: '3.1 tons/acre',
      recommendations: [
        'Monitor for soybean rust in the coming weeks',
        'Apply organic mulch to conserve soil moisture',
        'Consider crop rotation with small grains next season',
        'Implement precision fertilization based on yield maps'
      ],
      history: [
        { crop: 'Soybeans', season: 'Summer 2023', yield: 'In progress' },
        { crop: 'Corn', season: 'Summer 2022', yield: '9.2 tons/acre' },
        { crop: 'Winter Wheat', season: 'Winter 2021', yield: '3.5 tons/acre' }
      ]
    }
  ]);

  const [selectedLand, setSelectedLand] = useState(null);
  const [filterLocation, setFilterLocation] = useState('');
  const [filterCrop, setFilterCrop] = useState('');
  const [sortBy, setSortBy] = useState('healthIndex');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // New state for comparison feature
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [isComparing, setIsComparing] = useState(false);
  const [maxCompare] = useState(3); // Maximum number of lands to compare

  useEffect(() => {
    // Auto-select the first land on initial load if not comparing
    if (lands.length > 0 && !selectedLand && !isComparing) {
      setSelectedLand(lands[0]);
    }
  }, [lands, selectedLand, isComparing]);

  const getHealthColor = (score) => {
    if (score >= 80) return 'var(--accent-green)';
    if (score >= 60) return 'var(--accent-yellow)';
    return 'var(--accent-red)';
  };

  const handleSelectLand = (land) => {
    setSelectedLand(land);
  };

  const handleLocationFilter = (e) => {
    setFilterLocation(e.target.value);
  };

  const handleCropFilter = (e) => {
    setFilterCrop(e.target.value);
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    if (value === 'healthAsc') {
      setSortBy('healthIndex');
      setSortOrder('asc');
    } else if (value === 'healthDesc') {
      setSortBy('healthIndex');
      setSortOrder('desc');
    } else if (value === 'nameAsc') {
      setSortBy('name');
      setSortOrder('asc');
    } else if (value === 'nameDesc') {
      setSortBy('name');
      setSortOrder('desc');
    }
  };

  const filteredLands = lands
    .filter(land => {
      const matchesLocation = filterLocation === '' || 
                            land.location.toLowerCase().includes(filterLocation.toLowerCase());
      
      const matchesCrop = filterCrop === '' || 
                        land.currentCrop.toLowerCase().includes(filterCrop.toLowerCase());
      
      return matchesLocation && matchesCrop;
    })
    .sort((a, b) => {
      if (sortBy === 'healthIndex') {
        return sortOrder === 'asc' ? a.healthIndex - b.healthIndex : b.healthIndex - a.healthIndex;
      } else {
        // Sort by name
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();
        return sortOrder === 'asc' 
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }
    });

  const uniqueLocations = [...new Set(lands.map(land => land.location))];
  const uniqueCrops = [...new Set(lands.map(land => land.currentCrop))];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Wheat':
        return <FaSeedling />;
      case 'Corn':
        return <FaLeaf />;
      case 'Rice':
        return <FaWater />;
      case 'Soybeans':
        return <FaSeedling />;
      default:
        return <FaLeaf />;
    }
  };

  const exportReport = () => {
    if (!selectedLand) return;
    
    // In a real application, this would generate a PDF or CSV
    alert(`Exporting report for ${selectedLand.name}...`);
  };

  const viewOnMap = () => {
    if (!selectedLand) return;
    
    // In a real application, this would open a map view
    alert(`Opening map view for ${selectedLand.name} in ${selectedLand.location}`);
  };

  // Improved toggle selection function with better event handling
  const toggleLandSelection = (land, e) => {
    e.stopPropagation(); // Prevent triggering the card click
    
    setSelectedForComparison(prev => {
      // If already selected, remove it
      if (prev.some(item => item.id === land.id)) {
        return prev.filter(item => item.id !== land.id);
      }
      
      // If not selected and we haven't reached the max, add it
      if (prev.length < maxCompare) {
        return [...prev, land];
      }
      
      // If we've reached the max, show an alert and don't change
      alert(`You can only compare up to ${maxCompare} lands at a time.`);
      return prev;
    });
  };

  const startComparison = () => {
    if (selectedForComparison.length < 2) {
      alert('Please select at least 2 lands to compare.');
      return;
    }
    
    setIsComparing(true);
  };

  const resetComparison = () => {
    setIsComparing(false);
    setSelectedForComparison([]);
    
    // Auto-select the first land when exiting comparison mode
    if (lands.length > 0) {
      setSelectedLand(lands[0]);
    }
  };

  // Helper function to render comparison metrics
  const renderComparisonMetrics = () => {
    // If no lands are selected for comparison, show a message
    if (selectedForComparison.length === 0) {
      return (
        <div className="no-comparison-data">
          <FaInfoCircle className="empty-icon" />
          <p>No lands selected for comparison</p>
        </div>
      );
    }

    // If only one land is selected, prompt to select more
    if (selectedForComparison.length === 1) {
      return (
        <div className="no-comparison-data">
          <FaInfoCircle className="empty-icon" />
          <p>Please select at least one more land to compare</p>
        </div>
      );
    }

    return (
      <>
        {/* Basic Metrics Comparison */}
        <div className="comparison-section">
          <h4>Basic Information</h4>
          <div className="comparison-metrics-grid">
            <div className="comparison-metric-row">
              <div className="metric-label">Current Crop</div>
              {selectedForComparison.map(land => (
                <div key={land.id} className="metric-value">
                  <div className="metric-icon-small">
                    {getTypeIcon(land.currentCrop)}
                  </div>
                  {land.currentCrop}
                </div>
              ))}
            </div>
            
            <div className="comparison-metric-row">
              <div className="metric-label">Soil Type</div>
              {selectedForComparison.map(land => (
                <div key={land.id} className="metric-value">{land.soilType}</div>
              ))}
            </div>
            
            <div className="comparison-metric-row">
              <div className="metric-label">Last Rotation</div>
              {selectedForComparison.map(land => (
                <div key={land.id} className="metric-value">{land.lastRotation}</div>
              ))}
            </div>
            
            <div className="comparison-metric-row">
              <div className="metric-label">Yield Prediction</div>
              {selectedForComparison.map(land => (
                <div key={land.id} className="metric-value">{land.yieldPrediction}</div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Health Index Comparison */}
        <div className="comparison-section">
          <h4>Health Index Comparison</h4>
          <div className="health-comparison">
            {selectedForComparison.map(land => (
              <div key={land.id} className="health-comparison-item">
                <div className="health-comparison-label">{land.name}</div>
                <div className="health-comparison-bar-container">
                  <div 
                    className="health-comparison-bar"
                    style={{ 
                      width: `${land.healthIndex}%`,
                      backgroundColor: getHealthColor(land.healthIndex)
                    }}
                  ></div>
                </div>
                <div className="health-comparison-value" style={{ color: getHealthColor(land.healthIndex) }}>
                  {land.healthIndex}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Sustainability Factors Comparison */}
        <div className="comparison-section">
          <h4>Sustainability Factors</h4>
          
          <div className="sustainability-comparison">
            <div className="sustainability-factor-group">
              <div className="factor-comparison-label">Biodiversity</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 75 : land.id === 2 ? 60 : land.id === 3 ? 55 : 65;
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div 
                          className="factor-comparison-bar"
                          style={{ 
                            width: `${value}%`,
                            backgroundColor: getHealthColor(value)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{value}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="sustainability-factor-group">
              <div className="factor-comparison-label">Water Usage</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 85 : land.id === 2 ? 70 : land.id === 3 ? 50 : 65;
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div 
                          className="factor-comparison-bar"
                          style={{ 
                            width: `${value}%`,
                            backgroundColor: getHealthColor(value)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{value}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="sustainability-factor-group">
              <div className="factor-comparison-label">Soil Health</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 90 : land.id === 2 ? 65 : land.id === 3 ? 60 : 75;
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div 
                          className="factor-comparison-bar"
                          style={{ 
                            width: `${value}%`,
                            backgroundColor: getHealthColor(value)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{value}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Risk Assessment Comparison */}
        <div className="comparison-section">
          <h4>Risk Assessment</h4>
          
          <div className="risk-comparison">
            <div className="risk-factor-group">
              <div className="factor-comparison-label">Pest Pressure</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 30 : land.id === 2 ? 60 : land.id === 3 ? 85 : 45;
                  const label = land.id === 1 ? 'Low' : land.id === 2 ? 'Medium' : land.id === 3 ? 'High' : 'Medium';
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div 
                          className="factor-comparison-bar"
                          style={{ 
                            width: `${value}%`,
                            backgroundColor: getHealthColor(land.id === 1 ? 70 : land.id === 2 ? 40 : land.id === 3 ? 15 : 55)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="risk-factor-group">
              <div className="factor-comparison-label">Weather Vulnerability</div>
              <div className="factor-comparison-bars">
                {selectedForComparison.map(land => {
                  const value = land.id === 1 ? 50 : land.id === 2 ? 75 : land.id === 3 ? 55 : 40;
                  const label = land.id === 1 ? 'Medium' : land.id === 2 ? 'High' : land.id === 3 ? 'Medium' : 'Low';
                  return (
                    <div key={land.id} className="factor-comparison-item">
                      <div className="factor-comparison-name">{land.name}</div>
                      <div className="factor-comparison-bar-container">
                        <div 
                          className="factor-comparison-bar"
                          style={{ 
                            width: `${value}%`,
                            backgroundColor: getHealthColor(land.id === 1 ? 50 : land.id === 2 ? 25 : land.id === 3 ? 45 : 60)
                          }}
                        ></div>
                      </div>
                      <div className="factor-comparison-value">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Recommendations Comparison */}
        <div className="comparison-section">
          <h4>AI Recommendations</h4>
          <div className="recommendations-comparison">
            {selectedForComparison.map(land => (
              <div key={land.id} className="land-recommendations">
                <h5>{land.name} Recommendations</h5>
                <ul className="recommendations-list">
                  {land.recommendations.map((rec, index) => (
                    <li key={index} className="recommendation-item">{rec}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="crop-evaluation">
      <div className="section-header">
        <h2>Crop Evaluation</h2>
        {selectedForComparison.length > 0 && (
          <div className="comparison-controls">
            <span className="selected-count">
              {selectedForComparison.length} land{selectedForComparison.length !== 1 ? 's' : ''} selected
            </span>
            <button 
              className="compare-button"
              onClick={startComparison}
              disabled={selectedForComparison.length < 2}
            >
              <FaExchangeAlt /> Compare Lands
            </button>
          </div>
        )}
      </div>
      
      {isComparing ? (
        // Comparison View
        <div className="comparison-container">
          <div className="comparison-header">
            <h3>Land Comparison</h3>
            <button className="reset-button" onClick={resetComparison}>
              <FaTimes /> Exit Comparison
            </button>
          </div>
          
          <div className="comparison-content">
            {renderComparisonMetrics()}
          </div>
        </div>
      ) : (
        // Regular View
        <div className="evaluation-container">
          <div className="lands-sidebar">
            <div className="filter-section">
              <h3>Filter Lands</h3>
              <div className="filter-controls">
                <div className="filter-control">
                  <label>Location</label>
                  <select value={filterLocation} onChange={handleLocationFilter}>
                    <option value="">All Locations</option>
                    {uniqueLocations.map((location, index) => (
                      <option key={index} value={location}>{location}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-control">
                  <label>Current Crop</label>
                  <select value={filterCrop} onChange={handleCropFilter}>
                    <option value="">All Crops</option>
                    {uniqueCrops.map((crop, index) => (
                      <option key={index} value={crop}>{crop}</option>
                    ))}
                  </select>
                </div>
                
                <div className="filter-control">
                  <label>Sort By</label>
                  <select onChange={handleSortChange}>
                    <option value="healthDesc">Health Index (High to Low)</option>
                    <option value="healthAsc">Health Index (Low to High)</option>
                    <option value="nameAsc">Name (A to Z)</option>
                    <option value="nameDesc">Name (Z to A)</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="lands-list">
              {filteredLands.length > 0 ? (
                filteredLands.map(land => (
                  <div 
                    key={land.id} 
                    className={`land-card ${selectedLand?.id === land.id ? 'selected' : ''}`}
                    onClick={() => handleSelectLand(land)}
                  >
                    <div className="land-selection">
                      <div 
                        className="selection-checkbox"
                        onClick={(e) => toggleLandSelection(land, e)}
                      >
                        {selectedForComparison.some(item => item.id === land.id) 
                          ? <FaCheckSquare className="checkbox-icon checked" /> 
                          : <FaSquare className="checkbox-icon" />
                        }
                      </div>
                    </div>
                    
                    <div className="land-header">
                      <h4>{land.name}</h4>
                      <div className="land-location">
                        <FaMapMarkerAlt style={{ marginRight: '4px' }} /> {land.location}
                      </div>
                    </div>
                    
                    <div className="land-details">
                      <div className="land-crop">
                        {getTypeIcon(land.currentCrop)} {land.currentCrop}
                      </div>
                      <div className="land-size">{land.size}</div>
                    </div>
                    
                    <div className="land-health-bar">
                      <div 
                        className="health-indicator"
                        style={{ 
                          width: `${land.healthIndex}%`,
                          backgroundColor: getHealthColor(land.healthIndex)
                        }}
                      ></div>
                    </div>
                    
                    <div className="land-health-label">
                      <span>Health Index</span>
                      <span style={{ color: getHealthColor(land.healthIndex) }}>{land.healthIndex}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <p>No lands match your filter criteria</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="evaluation-details">
            {selectedLand ? (
              <>
                <div className="evaluation-header">
                  <div className="header-content">
                    <h3>{selectedLand.name}</h3>
                    <div className="header-meta">
                      <span className="header-location">
                        <FaMapMarkerAlt style={{ marginRight: '4px' }} /> {selectedLand.location}
                      </span>
                      <span className="header-size">{selectedLand.size}</span>
                    </div>
                  </div>
                  <div className="header-actions">
                    <button className="view-map-button" onClick={viewOnMap}>
                      View on Map
                    </button>
                    <button className="download-button" onClick={exportReport}>
                      <FaDownload /> Export Report
                    </button>
                  </div>
                </div>
                
                <div className="evaluation-metrics">
                  <div className="metric-card">
                    <div className="metric-icon">
                      <FaLeaf />
                    </div>
                    <div className="metric-details">
                      <div className="metric-label">Current Crop</div>
                      <div className="metric-value">{selectedLand.currentCrop}</div>
                    </div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-icon">
                      <FaChartLine />
                    </div>
                    <div className="metric-details">
                      <div className="metric-label">Yield Prediction</div>
                      <div className="metric-value">{selectedLand.yieldPrediction}</div>
                    </div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-icon">
                      <FaHistory />
                    </div>
                    <div className="metric-details">
                      <div className="metric-label">Last Rotation</div>
                      <div className="metric-value">{selectedLand.lastRotation}</div>
                    </div>
                  </div>
                  
                  <div className="metric-card">
                    <div className="metric-icon">
                      <FaFilter />
                    </div>
                    <div className="metric-details">
                      <div className="metric-label">Soil Type</div>
                      <div className="metric-value">{selectedLand.soilType}</div>
                    </div>
                  </div>
                </div>
                
                <div className="evaluation-sections">
                  <div className="evaluation-section">
                    <h4>Sustainability Assessment</h4>
                    <div className="sustainability-gauge">
                      <div className="gauge-value" style={{ color: getHealthColor(selectedLand.sustainabilityScore) }}>
                        {selectedLand.sustainabilityScore}
                      </div>
                      <div className="gauge-label">Sustainability Score</div>
                    </div>
                    <div className="sustainability-factors">
                      <div className="factor">
                        <div className="factor-label">
                          <span>Biodiversity</span>
                          <span>{selectedLand.id === 1 ? 75 : selectedLand.id === 2 ? 60 : 55}%</span>
                        </div>
                        <div className="factor-bar">
                          <div 
                            className="factor-indicator"
                            style={{ 
                              width: `${selectedLand.id === 1 ? 75 : selectedLand.id === 2 ? 60 : 55}%`,
                              backgroundColor: getHealthColor(selectedLand.id === 1 ? 75 : selectedLand.id === 2 ? 60 : 55)
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="factor">
                        <div className="factor-label">
                          <span>Water Usage</span>
                          <span>{selectedLand.id === 1 ? 85 : selectedLand.id === 2 ? 70 : 50}%</span>
                        </div>
                        <div className="factor-bar">
                          <div 
                            className="factor-indicator"
                            style={{ 
                              width: `${selectedLand.id === 1 ? 85 : selectedLand.id === 2 ? 70 : 50}%`,
                              backgroundColor: getHealthColor(selectedLand.id === 1 ? 85 : selectedLand.id === 2 ? 70 : 50)
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="factor">
                        <div className="factor-label">
                          <span>Soil Health</span>
                          <span>{selectedLand.id === 1 ? 90 : selectedLand.id === 2 ? 65 : 60}%</span>
                        </div>
                        <div className="factor-bar">
                          <div 
                            className="factor-indicator"
                            style={{ 
                              width: `${selectedLand.id === 1 ? 90 : selectedLand.id === 2 ? 65 : 60}%`,
                              backgroundColor: getHealthColor(selectedLand.id === 1 ? 90 : selectedLand.id === 2 ? 65 : 60)
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="evaluation-section">
                    <h4>AI Recommendations</h4>
                    <ul className="recommendations-list">
                      {selectedLand.recommendations.map((rec, index) => (
                        <li key={index} className="recommendation-item">{rec}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="evaluation-section">
                    <h4>Crop History</h4>
                    <div className="history-table">
                      <div className="history-header">
                        <div className="history-cell">Season</div>
                        <div className="history-cell">Crop</div>
                        <div className="history-cell">Yield</div>
                      </div>
                      {selectedLand.history.map((entry, index) => (
                        <div key={index} className="history-row">
                          <div className="history-cell">{entry.season}</div>
                          <div className="history-cell">
                            {getTypeIcon(entry.crop)} {entry.crop}
                          </div>
                          <div className="history-cell">{entry.yield}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="evaluation-section">
                    <h4>Risk Assessment</h4>
                    <div className="risk-factors">
                      <div className="factor">
                        <div className="factor-label">
                          <span>Pest Pressure</span>
                          <span>{selectedLand.id === 1 ? 'Low' : selectedLand.id === 2 ? 'Medium' : 'High'}</span>
                        </div>
                        <div className="factor-bar">
                          <div 
                            className="factor-indicator"
                            style={{ 
                              width: `${selectedLand.id === 1 ? 30 : selectedLand.id === 2 ? 60 : 85}%`,
                              backgroundColor: getHealthColor(selectedLand.id === 1 ? 70 : selectedLand.id === 2 ? 40 : 15)
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="factor">
                        <div className="factor-label">
                          <span>Weather Vulnerability</span>
                          <span>{selectedLand.id === 1 ? 'Medium' : selectedLand.id === 2 ? 'High' : 'Medium'}</span>
                        </div>
                        <div className="factor-bar">
                          <div 
                            className="factor-indicator"
                            style={{ 
                              width: `${selectedLand.id === 1 ? 50 : selectedLand.id === 2 ? 75 : 55}%`,
                              backgroundColor: getHealthColor(selectedLand.id === 1 ? 50 : selectedLand.id === 2 ? 25 : 45)
                            }}
                          ></div>
                        </div>
                      </div>
                      <div className="factor">
                        <div className="factor-label">
                          <span>Market Volatility</span>
                          <span>{selectedLand.id === 1 ? 'Medium' : selectedLand.id === 2 ? 'Low' : 'High'}</span>
                        </div>
                        <div className="factor-bar">
                          <div 
                            className="factor-indicator"
                            style={{ 
                              width: `${selectedLand.id === 1 ? 45 : selectedLand.id === 2 ? 25 : 70}%`,
                              backgroundColor: getHealthColor(selectedLand.id === 1 ? 55 : selectedLand.id === 2 ? 75 : 30)
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-land-selected">
                <FaSearchPlus className="empty-icon" />
                <p>Select a land to view detailed evaluation</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CropEvaluation; 